import React, { useEffect, useState } from 'react';
import { UsersIcon, ActivityIcon, HeartIcon, MedalIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { FeatureCard } from '../components/FeatureCard';
import { ActivityCard } from '../components/ActivityCard';
import { ValuesSection } from '../components/ValuesSection';
import { CoachCard, Coach } from '../components/CoachCard';
import { CoachModal } from '../components/CoachModal';
export function HomePage() {
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const features = [{
    icon: <ActivityIcon className="w-6 h-6" data-id="element-885" />,
    title: 'Multi-Sport Development',
    description: 'Exposure to multiple sports builds well-rounded athletes with diverse skills.'
  }, {
    icon: <UsersIcon className="w-6 h-6" data-id="element-886" />,
    title: 'Confidence & Friendships',
    description: 'Our sessions foster self-belief, teamwork, and lasting friendships.'
  }, {
    icon: <ZapIcon className="w-6 h-6" data-id="element-887" />,
    title: 'Motor Skills',
    description: 'Age-appropriate activities develop coordination, agility, and physical literacy.'
  }, {
    icon: <MedalIcon className="w-6 h-6" data-id="element-888" />,
    title: 'Expert-Led Programmes',
    description: 'Every session designed and delivered by qualified Olympian coaches.'
  }, {
    icon: <TargetIcon className="w-6 h-6" data-id="element-889" />,
    title: 'Coaching for Every Age',
    description: 'Tailored programmes from early years through to competitive juniors.'
  }, {
    icon: <HeartIcon className="w-6 h-6" data-id="element-890" />,
    title: 'Healthy Body & Mind',
    description: 'Building lifelong habits for physical and mental wellbeing.'
  }];
  const activities = [{
    title: 'Badminton',
    description: 'From first racket to competitive play, our Olympian-led badminton programme develops technique, agility, and game sense.',
    slug: 'badminton',
    colorAccent: 'blue' as const,
    icon: <ActivityIcon className="w-32 h-32" data-id="element-891" />,
    image: 'https://cdn.magicpatterns.com/uploads/h1HjTQtx6uRzRJncSSv8d2/badminton.webp'
  }, {
    title: 'Multi-Sports',
    description: 'A dynamic mix of sports and activities designed to build all-round athletic ability and a love of movement.',
    slug: 'multi-sports',
    colorAccent: 'coral' as const,
    icon: <TargetIcon className="w-32 h-32" data-id="element-892" />,
    image: 'https://cdn.magicpatterns.com/uploads/nius5mV4GSJtkuCgu886pQ/Multisports.webp'
  }, {
    title: 'Netball',
    description: 'Build court skills, tactical awareness, and team spirit in our inclusive netball sessions for all abilities.',
    slug: 'netball',
    colorAccent: 'green' as const,
    icon: <UsersIcon className="w-32 h-32" data-id="element-893" />,
    image: 'https://cdn.magicpatterns.com/uploads/4gCkFMntbwDcZHYhPNEDey/Netball.jpg'
  }];
  const coaches: Coach[] = [{
    id: '1',
    name: 'Donna Kellogg',
    role: 'Lead Coach',
    credentials: 'BSc (Hons) MBE OLY',
    image: 'https://cdn.magicpatterns.com/uploads/4ASxFGF7ivucC7XtmD1BzK/donna_pic_cropped-768x767.webp',
    bio: 'Triple Olympian who represented Team GB at the 2000, 2004 and 2008 Olympic Games. Multiple world, Commonwealth and European championship medallist.',
    fullBio: "Donna is a fantastic sporting role model for all our Bright Star Sports children. She is a triple Olympian having represented Team GB at the 2000, 2004 and 2008 Olympic Games and is a multiple world, Commonwealth and European championship medallist. Donna has loved her life in sport and hopes to inspire more young children to have fun, build their confidence and value the friendships they make whilst participating in sport and physical activity.\n\nShe has coached all ages and abilities over the years having been involved with school programmes, holiday camps and coaching workshops. Donna's two young daughters enjoy trying out the latest content in our Bright Star Sports sessions and give Mum plenty of feedback.",
    achievements: ['Triple Olympian (2000, 2004, 2008)', 'Multiple World Championship Medallist', 'Commonwealth Championship Medallist', 'European Championship Medallist', 'Awarded MBE'],
    philosophy: 'I hope to inspire more young children to have fun, build their confidence and value the friendships they make whilst participating in sport and physical activity.'
  }, {
    id: '2',
    name: 'Natalie Barton',
    role: 'Lead Coach',
    credentials: 'BSc (Hons) OLY',
    image: 'https://cdn.magicpatterns.com/uploads/ocRMerKbd1woFGMKYSm6JS/natalie_pic_cropped-768x767.webp',
    bio: "Multi-talented sportswoman who represented Team GB at the 2004 Olympic Games. Former international badminton player with a career-high ranking inside the world's top 5.",
    fullBio: "Natalie is a multi-talented sportswoman having competed in many sports to a high level and represented Team GB at the 2004 Olympic Games. She is a former international badminton player with a career-high ranking inside the world's top 5 and still enjoys competing for Hertfordshire in the Inter Counties Premiership.\n\nNatalie is a highly accomplished PE teacher, delivering a full curriculum of sports to children aged 3 to 18. She ensures all Bright Star Sports workshops and classes create a supportive and fun environment where children can come and explore new sports, learn new skills and be inspired and encouraged to achieve their goals.",
    achievements: ['Olympic Games Representative (2004)', 'World Top 5 Ranking', 'Inter Counties Premiership Competitor', 'Accomplished PE Teacher'],
    philosophy: 'Creating a supportive and fun environment where children can explore new sports, learn new skills and be inspired and encouraged to achieve their goals.'
  }, {
    id: '3',
    name: 'Sarah Chapman',
    role: 'Lead Coach',
    credentials: 'MSc BSc (Hons) OLY',
    image: 'https://cdn.magicpatterns.com/uploads/pQyrJ9Go8cTY1DPUpopDaC/sarah_chapman_cropped-768x767.webp',
    bio: 'Team GB Olympic star who competed at the Sydney 2000 Olympic Games. European championship medallist with a career-high ranking inside the world top 10.',
    fullBio: 'Sarah is another of our Team GB Olympic stars and competed at the Sydney 2000 Olympic Games. She is a European championship medallist, won multiple international badminton tournaments and achieved a career-high ranking inside the world top 10.\n\nShe has worked for the past 20 years as a musculoskeletal physiotherapist and uses her rehab and strength and conditioning knowledge to build badminton-specific speed, strength and stability skills in young athletes.\n\nSarah is a Badminton England UKCC Level 2 Coach and has many years of badminton coaching experience from beginners through to county-level and junior international players.',
    achievements: ['Olympic Games Representative (Sydney 2000)', 'European Championship Medallist', 'World Top 10 Ranking', 'Badminton England UKCC Level 2 Coach', 'Musculoskeletal Physiotherapist'],
    philosophy: 'Using rehab and strength and conditioning knowledge to build badminton-specific speed, strength and stability skills in young athletes.'
  }];
  return <div className="w-full overflow-hidden pt-20" data-id="element-894">
      {/* SECTION 1: HERO */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] bg-brand-blue overflow-hidden" data-id="element-895">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-896" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-897"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-898">
          <div className="max-w-3xl" data-id="element-899">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-900">
              Inspire. Encourage.{' '}
              <span className="text-brand-yellow" data-id="element-901">Achieve.</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl leading-relaxed" data-id="element-902">
              Expert sport coaching led by our Olympian coaches Donna Kellogg
              MBE, Natalie Barton and Sarah Chapman, building confidence,
              skills, and friendships for young athletes across the UK.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10" data-id="element-903">
              <Button href="/book" variant="primary" size="lg" data-id="element-904">
                Book a Session
              </Button>
              <Button href="#mission" variant="secondary" size="lg" className="border-white text-white hover:bg-white/20" data-id="element-905">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: MISSION / BENEFITS */}
      <section id="mission" className="py-24 bg-navy-50" data-id="element-906">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-907">
          <SectionHeader eyebrow="Our Mission" title="From Grassroots to Elite" description="We believe every child deserves access to world-class coaching. Our programmes are designed to develop not just better athletes, but confident, resilient young people." data-id="element-908" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-909">
            {features.map((feature, index) => <FeatureCard key={index} icon={feature.icon} title={feature.title} description={feature.description} index={index} data-id="element-910" />)}
          </div>
        </div>
      </section>

      {/* SECTION 3: ACTIVITIES */}
      <section className="py-24 bg-white" data-id="element-911">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-912">
          <SectionHeader eyebrow="What We Offer" title="Our Programmes" description="Discover our range of expertly designed coaching sessions, tailored for different age groups and skill levels." data-id="element-913" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-914">
            {activities.map((activity, index) => <ActivityCard key={index} {...activity} index={index} data-id="element-915" />)}
          </div>
        </div>
      </section>

      {/* SECTION 4: OLYMPIC VALUES */}
      <ValuesSection ctaLabel="Discover Our Medals Programme" data-id="element-916" />

      {/* SECTION 5: MEET THE TEAM */}
      <section className="py-24 bg-navy-50" data-id="element-917">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-918">
          <SectionHeader eyebrow="Our Coaches" title="Meet the Team" description="Learn from the best. Our lead coaches Donna Kellogg MBE, Natalie Barton and Sarah Chapman are all Team GB Olympians dedicated to inspiring the next generation through sport." data-id="element-919" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-920">
            {coaches.map((coach, index) => <CoachCard key={coach.id} coach={coach} index={index} onViewProfile={setSelectedCoach} data-id="element-921" />)}
          </div>
        </div>
      </section>

      {/* Coach Modal Portal */}
      <CoachModal coach={selectedCoach} isOpen={!!selectedCoach} onClose={() => setSelectedCoach(null)} data-id="element-922" />
    </div>;
}